"use client";

import {
  FormControl,
  Label,
  LabelText,
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
  ValidationErrorMessage,
} from "@sourceful/design-system-v3";
import { SanityFormDropdownField } from "@sourceful/shared-utils/sanity-utils";
import { useCallback, useState } from "react";
import SanityRequiredFieldIndicator from "./SanityRequiredFieldIndicator";
import { FieldMeta } from "@tanstack/react-form";

interface SanityFormDropdownProps {
  field: SanityFormDropdownField;
  className?: string;
  defaultValue?: string;
  fieldMeta?: FieldMeta;
  onChange?: (e: any) => void;
}

export default function SanityFormDropdown({
  field,
  className,
  defaultValue,
  fieldMeta,
  onChange,
}: Readonly<SanityFormDropdownProps>) {
  const [value, setValue] = useState<string | undefined>(defaultValue);
  const [valid, setValid] = useState<boolean>(true);

  // see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevValue, setPrevValue] = useState(defaultValue);
  if (defaultValue !== prevValue) {
    setPrevValue(defaultValue);
    setValue(defaultValue);
  }

  const handleValueChange = useCallback(
    (value: string) => {
      onChange && onChange(value);
      setValue(value);
    },
    [onChange, setValue]
  );

  const checkValue = useCallback(() => {
    if (value === undefined) {
      setValid(false);
    } else {
      setValid(true);
    }
  }, [value]);

  const error = !!fieldMeta?.errors.length || !valid;

  return (
    <FormControl layout="column" className={className}>
      <Label labelFor={field.id}>
        <LabelText>
          {field.label}
          {field.isRequired ? <SanityRequiredFieldIndicator /> : ""}
        </LabelText>
      </Label>
      <Select
        name={field.id}
        required={field.isRequired}
        value={value}
        onValueChange={handleValueChange}
      >
        <SelectTrigger error={error} id={field.id} onFocus={checkValue}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {field?.options?.map(option => (
            <SelectItem key={option.value} value={option.value}>
              <SelectItemText>{option.label}</SelectItemText>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {fieldMeta?.errors ? (
        <ValidationErrorMessage inputId={field.id}>{fieldMeta.errors}</ValidationErrorMessage>
      ) : (
        ""
      )}
    </FormControl>
  );
}
